import { Check, Lock, MailCheck, MailOpen, Undo2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ConversationDetail } from '@/shared/api';
import { Button } from '@/shared/components';
import { useOnline } from '@/shared/hooks/useOnline';

/** Aktionen im Kopf einer Unterhaltung; jede erscheint nur, wo sie hier möglich ist. */
export function ConversationActions({
  thread,
  onClose,
  onNoReplyNeeded,
  onMarkRead,
  onLocalResolution,
  resolving,
}: {
  thread: ConversationDetail;
  onClose?: () => void;
  onNoReplyNeeded?: () => void;
  /** Nur im Support-Modus gesetzt: dort markiert das Ansehen allein nichts als gelesen. */
  onMarkRead?: () => void;
  onLocalResolution?: () => void;
  resolving?: boolean;
}) {
  const { t } = useTranslation('conversationResolution');
  const online = useOnline();
  return (
    <div className="flex basis-full flex-wrap items-center gap-2 sm:basis-auto">
      {onLocalResolution && (
        <Button
          type="button"
          variant={thread.isLocallyResolved ? 'secondary' : 'brand'}
          className="min-h-11 whitespace-normal"
          leftIcon={
            thread.isLocallyResolved ? (
              <Undo2 size={14} aria-hidden />
            ) : (
              <Check size={14} aria-hidden />
            )
          }
          onClick={onLocalResolution}
          loading={resolving}
          disabled={!online}
          title={!online ? t('offline') : undefined}
        >
          {t(thread.isLocallyResolved ? 'undo' : 'resolve')}
        </Button>
      )}
      {onMarkRead && (
        <Button
          type="button"
          variant="secondary"
          className="rounded-pill min-h-11"
          leftIcon={<MailOpen size={14} aria-hidden />}
          onClick={onMarkRead}
        >
          {t('markRead')}
        </Button>
      )}
      {onNoReplyNeeded && thread.canMarkNoReplyNeeded && !thread.isClosed && (
        <Button
          type="button"
          variant="secondary"
          className="rounded-pill min-h-11"
          leftIcon={<MailCheck size={14} aria-hidden />}
          onClick={onNoReplyNeeded}
          disabled={!online || resolving}
        >
          {t('noReplyChannel')}
        </Button>
      )}
      {onClose && thread.canClose && !thread.isClosed && (
        <Button
          type="button"
          variant="ghost"
          className="rounded-pill border-ink min-h-11 border"
          leftIcon={<Lock size={14} aria-hidden />}
          onClick={onClose}
          disabled={!online || resolving}
        >
          {t('closeChannel')}
        </Button>
      )}
    </div>
  );
}
